import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  LinearProgress,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../../components/common/dialog/BootstrapDialog';
import EmpSelectbox from '../../../components/common/form/autocomplete/EmpSelectbox';
import * as fileApi from '../../../lib/api/file';
import { useFormik } from 'formik';

const ProductionDetailDialog = ({ open, onClose, productionData, onUpdate, onStatusChange, planData }) => {
  const [products, setProducts] = useState([]);
  const [designFiles, setDesignFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [history, setHistory] = useState([]);
  const [isEditingDesigner, setIsEditingDesigner] = useState(false);
  
  // 파일 업로드 로딩 상태
  const [fileUploading, setFileUploading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      designerUuid: '',
    },
    onSubmit: (values) => {
      // 담당자 변경 처리는 handleSave에서 처리
    },
  });

  useEffect(() => {
    if (productionData) {
      // 제품 리스트 초기화 (생산 완료 상태 포함)
      const productsWithStatus = (productionData.products || []).map(product => ({
        ...product,
        isCompleted: product.isCompleted || false,
        completedDate: product.completedDate || '',
        completedQuantity: product.completedQuantity || 0,
      }));
      setProducts(productsWithStatus);
      setDesignFiles([]);
      setNewFiles([]);
      setHistory(productionData.history || []);
      setIsEditingDesigner(false);
      formik.setFieldValue('designerUuid', productionData.designerUuid || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionData]);

  // 파일 목록 로드
  useEffect(() => {
    const fetchFiles = async () => {
      if (!open || !productionData?.productionId) return;
      
      setFileLoading(true);
      try {
        const response = await fileApi.getProductionFiles(productionData.productionId);
        if (response.data && response.data.list) {
          const files = response.data.list.map(file => ({
            id: file.fileUuid,
            fileUuid: file.fileUuid,
            fileName: file.fileNm,
            fileSize: file.fileSize ? `${(file.fileSize / 1024).toFixed(1)}KB` : '-',
            uploadDate: file.crtDt ? file.crtDt.split(' ')[0] : '-',
            fileUrl: file.fileUrl,
          }));
          setDesignFiles(files);
        }
      } catch (err) {
        console.error('파일 목록 조회 에러:', err);
        setDesignFiles([]);
      } finally {
        setFileLoading(false);
      }
    };
    
    fetchFiles();
  }, [open, productionData?.productionId]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // productionId가 없으면 newFiles에만 추가 (저장 시 업로드)
    if (!productionData?.productionId) {
      const fileObjects = files.map((file, index) => ({
        id: `temp-${Date.now()}-${index}`,
        fileName: file.name,
        fileSize: `${(file.size / 1024).toFixed(1)}KB`,
        uploadDate: new Date().toISOString().split('T')[0],
        file: file,
        isNew: true,
      }));
      setNewFiles([...newFiles, ...fileObjects]);
      return;
    }
    
    // productionId가 있으면 즉시 업로드
    setFileUploading(true);
    try {
      for (const file of files) {
        const response = await fileApi.uploadProductionFile(
          file,
          planData?.projectId || '',
          planData?.planId || productionData?.planId || '',
          productionData?.productionId,
          ''
        );
        
        if (response.data && response.data.result === 'success') {
          const uploadedFile = response.data.info;
          setDesignFiles(prev => [...prev, {
            id: uploadedFile.fileUuid,
            fileUuid: uploadedFile.fileUuid,
            fileName: uploadedFile.fileNm,
            fileSize: uploadedFile.fileSize ? `${(uploadedFile.fileSize / 1024).toFixed(1)}KB` : '-',
            uploadDate: new Date().toISOString().split('T')[0],
            fileUrl: uploadedFile.fileUrl,
          }]);
        }
      }
    } catch (err) {
      console.error('파일 업로드 에러:', err);
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setFileUploading(false);
      event.target.value = '';
    }
  };

  const handleFileDelete = async (fileId, isNew) => {
    if (isNew) {
      setNewFiles(newFiles.filter((f) => f.id !== fileId));
      return;
    }
    
    // 서버에서 파일 삭제
    if (window.confirm('파일을 삭제하시겠습니까?')) {
      try {
        await fileApi.removeFile(fileId);
        setDesignFiles(designFiles.filter((f) => f.id !== fileId));
      } catch (err) {
        console.error('파일 삭제 에러:', err);
        alert('파일 삭제에 실패했습니다.');
      }
    }
  };

  // 파일 다운로드 핸들러
  const handleFileDownload = async (file) => {
    try {
      const response = await fileApi.downloadFile(file.fileUuid || file.id);
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('파일 다운로드 에러:', err);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  const handleProductComplete = (index, checked) => {
    const updatedProducts = [...products];
    updatedProducts[index].isCompleted = checked;
    if (checked) {
      updatedProducts[index].completedDate = new Date().toISOString().split('T')[0];
      updatedProducts[index].completedQuantity = updatedProducts[index].quantity;
    } else {
      updatedProducts[index].completedDate = '';
      updatedProducts[index].completedQuantity = 0;
    }
    setProducts(updatedProducts);
  };

  const handleQuantityChange = (index, value) => {
    const updatedProducts = [...products];
    const newQty = parseInt(value) || 0;
    updatedProducts[index].completedQuantity = Math.min(
      Math.max(0, newQty),
      updatedProducts[index].quantity
    );
    setProducts(updatedProducts);
  };

  const addHistory = (action, description) => {
    const newHistoryItem = {
      timestamp: new Date().toISOString(),
      user: '현재 사용자', // TODO: 실제 로그인 사용자 정보로 교체
      action: action,
      description: description,
    };
    return [...history, newHistoryItem];
  };

  const getProductionMetrics = (markCompleted = false) => {
    const totalQuantity = products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
    const hasProducts = products.length > 0;
    const fallbackProducedQuantity = markCompleted
      ? (Number(productionData.targetQuantity) || 0)
      : (Number(productionData.producedQuantity) || 0);
    const producedQuantity = hasProducts
      ? products.reduce((sum, p) => sum + (Number(p.completedQuantity) || 0), 0)
      : fallbackProducedQuantity;
    const targetQuantity = hasProducts
      ? totalQuantity
      : (Number(productionData.targetQuantity) || 0);
    const progress = targetQuantity > 0
      ? Math.round((producedQuantity / targetQuantity) * 100)
      : 0;

    return {
      targetQuantity,
      producedQuantity,
      progress,
    };
  };

  const handleSave = async () => {
    const { targetQuantity, producedQuantity, progress } = getProductionMetrics();

    // 변경 사항 기록
    let newHistory = [...history];
    const changes = [];

    // 담당자 변경 확인
    if (formik.values.designerUuid && formik.values.designerUuid !== productionData.designerUuid) {
      changes.push('담당자 변경');
      newHistory = addHistory('담당자 변경', `담당자가 변경되었습니다.`);
    }

    // 제품 완료 상태 변경 확인
    const originalProducts = productionData.products || [];
    products.forEach((product, index) => {
      const original = originalProducts[index];
      if (original && original.isCompleted !== product.isCompleted) {
        if (product.isCompleted) {
          newHistory = addHistory('제품 완료', `${product.productName} 생산 완료`);
        } else {
          newHistory = addHistory('제품 완료 취소', `${product.productName} 완료 취소`);
        }
      }
    });

    // 파일 추가 확인
    if (newFiles.length > 0) {
      newHistory = addHistory('파일 추가', `${newFiles.length}개의 파일이 추가되었습니다.`);
    }

    if (changes.length === 0 && newHistory.length === history.length && newFiles.length === 0) {
      newHistory = addHistory('저장', '생산 진행 상황 저장');
    }

    const updatedData = {
      ...productionData,
      designerUuid: formik.values.designerUuid || productionData.designerUuid,
      products: products,
      designFiles: [...designFiles, ...newFiles],
      targetQuantity,
      producedQuantity,
      defectQuantity: productionData.defectQuantity || 0,
      progress: progress,
      history: newHistory,
    };
    
    const success = await onUpdate(updatedData);
    if (!success) {
      return;
    }
    setIsEditingDesigner(false);
    onClose();
  };

  const handleComplete = async () => {
    // 상태 변경 API 호출 (onStatusChange 사용)
    if (onStatusChange) {
      const { targetQuantity, producedQuantity, progress } = getProductionMetrics(true);
      const updatedData = {
        ...productionData,
        designerUuid: formik.values.designerUuid || productionData.designerUuid,
        products: products,
        designFiles: [...designFiles, ...newFiles],
        targetQuantity,
        producedQuantity,
        defectQuantity: productionData.defectQuantity || 0,
        progress,
      };

      if (onUpdate) {
        const updateSuccess = await onUpdate(updatedData);
        if (!updateSuccess) {
          return;
        }
      }

      const success = await onStatusChange(
        productionData.productionId || productionData.resultId || productionData.id,
        'COMPLETED',
        '생산 완료'
      );
      
      if (success) {
        onClose();
      }
    } else {
      // onStatusChange가 없으면 기존 방식 (fallback)
      const { targetQuantity, producedQuantity, progress } = getProductionMetrics(true);
      
      let newHistory = addHistory('생산 완료', '모든 제품 생산이 완료되었습니다.');
      
      if (newFiles.length > 0) {
        newHistory = addHistory('파일 추가', `${newFiles.length}개의 파일이 추가되었습니다.`);
      }
      
      const updatedData = {
        ...productionData,
        designerUuid: formik.values.designerUuid || productionData.designerUuid,
        products: products,
        designFiles: [...designFiles, ...newFiles],
        targetQuantity,
        producedQuantity,
        defectQuantity: productionData.defectQuantity || 0,
        progress: progress,
        status: 'COMPLETED',
        actualEndDate: new Date().toISOString().split('T')[0],
        history: newHistory,
      };
      
      onUpdate(updatedData);
      onClose();
    }
  };

  const handleClose = () => {
    setNewFiles([]);
    onClose();
  };

  if (!productionData) return null;

    const isStatusCompleted = productionData?.status === 'COMPLETED' || productionData?.status === '생산완료';

  const getStatusColor = (status) => {
    const colors = {
      '대기': '#9e9e9e',
      'PENDING': '#9e9e9e',
      '생산중': '#ff9800',
      'IN_PROGRESS': '#ff9800',
      '생산완료': '#4caf50',
      'COMPLETED': '#4caf50',
      '중단': '#f44336',
      'SUSPENDED': '#f44336',
      'CANCELLED': '#f44336',
    };
    return colors[status] || '#9e9e9e';
  };

  const completedCount = products.filter(p => p.isCompleted).length;
  const totalCount = products.length;
  const isAllCompleted = completedCount === totalCount && totalCount > 0;

  return (
    <BootstrapDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <BootstrapDialogTitle onClose={handleClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="primary" />
            <Typography variant="h6">생산 상세</Typography>
            <Chip 
              label={productionData.manuNo} 
              size="small" 
              color="default" 
              variant="outlined"
            />
          </Box>
          <Chip
            label={productionData.status}
            sx={{
              backgroundColor: getStatusColor(productionData.status),
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Box>
      </BootstrapDialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <Grid container spacing={3}>
            {/* 진행 상황 요약 */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  전체 진행률
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={productionData.progress || 0}
                      sx={{ height: 10, borderRadius: 1 }}
                      color={isAllCompleted ? 'success' : 'primary'}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {productionData.progress || 0}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* 제품 리스트 테이블 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InventoryIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  제품별 생산 현황 ({completedCount}/{totalCount})
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              {products.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: 60 }} align="center">
                          완료
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>제품명</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>규격</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 80 }} align="center">
                          단위
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">
                          목표수량
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">
                          생산수량
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">
                          완료일자
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product, index) => (
                        <TableRow 
                          key={index}
                          sx={{
                            bgcolor: product.isCompleted ? '#e8f5e9' : 'inherit',
                          }}
                        >
                          <TableCell align="center">
                            <Checkbox
                              checked={product.isCompleted}
                              onChange={(e) => handleProductComplete(index, e.target.checked)}
                              color="success"
                              disabled={isStatusCompleted}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {product.productName}
                              {product.isCompleted && (
                                <CheckCircleIcon 
                                  fontSize="small" 
                                  sx={{ color: 'success.main' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{product.spec}</TableCell>
                          <TableCell align="center">{product.unit}</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={product.quantity}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isStatusCompleted ? (
                              <Typography variant="body2">
                                {product.completedQuantity}
                              </Typography>
                            ) : (
                              <TextField
                                type="number"
                                size="small"
                                value={product.completedQuantity}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                inputProps={{ 
                                  min: 0, 
                                  max: product.quantity,
                                  style: { textAlign: 'center' }
                                }}
                                sx={{ width: 80 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {product.completedDate || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  생산할 제품이 없습니다.
                </Alert>
              )}
            </Grid>

            {/* 기본 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                기본 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="생산 구분"
                value={productionData.type === 'INTERNAL' ? '내부' : productionData.type === 'EXTERNAL' ? '외주' : productionData.typeLabel || productionData.type}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ position: 'relative' }}>
                {isEditingDesigner && !isStatusCompleted ? (
                  <EmpSelectbox
                    formik={formik}
                    name="designerUuid"
                    label="담당자 *"
                    variant="outlined"
                    margin="none"
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="담당자"
                    value={productionData.managerName || productionData.worker || productionData.designer || '-'}
                    InputProps={{ 
                      readOnly: true,
                      endAdornment: !isStatusCompleted ? (
                        <IconButton 
                          onClick={() => setIsEditingDesigner(true)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : null,
                    }}
                    variant="filled"
                  />
                )}
                {isEditingDesigner && (
                  <Button 
                    size="small" 
                    onClick={() => setIsEditingDesigner(false)}
                    sx={{ mt: 0.5 }}
                  >
                    취소
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="시작일"
                value={productionData.createdAt}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="변경일"
                value={productionData.updatedAt}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>

            {/* 설계 파일 섹션 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <AttachFileIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  생산 파일 ({(designFiles.length + newFiles.length)})
                </Typography>
                {fileLoading && <CircularProgress size={20} />}
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                {/* 파일 업로드 영역 */}
                {!isStatusCompleted && (
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ height: 100, borderStyle: 'dashed', borderWidth: 2 }}
                      disabled={fileUploading}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        {fileUploading ? (
                          <>
                            <CircularProgress size={30} sx={{ mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">파일 업로드 중...</Typography>
                          </>
                        ) : (
                          <>
                            <AttachFileIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              클릭하여 파일을 선택하거나 드래그 앤 드롭하세요
                            </Typography>
                          </>
                        )}
                      </Box>
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileUpload}
                        accept=".dwg,.dxf,.pdf,.xlsx,.docx,.jpg,.png,.zip"
                      />
                    </Button>
                  </Box>
                )}

                {/* 업로드된 파일 목록 */}
                {(designFiles.length > 0 || newFiles.length > 0) ? (
                  <Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 2 }}>
                      첨부 파일 목록
                    </Typography>
                    <List>
                      {/* 기존 파일 */}
                      {designFiles.map((file) => (
                        <ListItem
                          key={file.id}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                          }}
                          secondaryAction={
                            <Box>
                              <IconButton edge="end" sx={{ mr: 1 }} onClick={() => handleFileDownload(file)}>
                                <DownloadIcon />
                              </IconButton>
                              {!isStatusCompleted && (
                                <IconButton
                                  edge="end"
                                  onClick={() => handleFileDelete(file.id, false)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <FileIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={file.fileName}
                            secondary={`${file.fileSize} | ${file.uploadDate}`}
                          />
                        </ListItem>
                      ))}

                      {/* 새로 추가된 파일 */}
                      {newFiles.map((file) => (
                        <ListItem
                          key={file.id}
                          sx={{
                            border: 1,
                            borderColor: 'success.main',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: 'success.50',
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => handleFileDelete(file.id, true)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            <FileIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {file.fileName}
                                <Chip label="NEW" size="small" color="success" />
                              </Box>
                            }
                            secondary={`${file.fileSize} | 업로드 대기중`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Alert severity="info">
                    첨부된 파일이 없습니다. {!isStatusCompleted && '파일을 업로드해주세요.'}
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* 비고 */}
            {productionData.remarks && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="비고"
                  value={productionData.remarks}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
            )}

            {/* 변경 이력 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <HistoryIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  변경 이력 ({history.length})
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              {history.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: 150 }}>일시</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 120 }}>작업자</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 120 }}>작업</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>내용</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.slice().reverse().map((item, index) => {
                        const actionColors = {
                          '제품 완료': 'success',
                          '제품 완료 취소': 'warning',
                          '담당자 변경': 'info',
                          '생산 완료': 'success',
                          '파일 추가': 'info',
                          '저장': 'default',
                        };
                        const actionColor = actionColors[item.action] || 'default';

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(item.timestamp).toLocaleString('ko-KR')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="action" />
                                {item.user}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.action} 
                                size="small" 
                                color={actionColor}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{item.description}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  변경 이력이 없습니다.
                </Alert>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} size="large">
          닫기
        </Button>
        <Box sx={{ flex: 1 }} />
        
        {!isStatusCompleted && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSave}
              size="large"
            >
              저장
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={handleComplete}
              disabled={!isAllCompleted}
              size="large"
              startIcon={<CheckCircleIcon />}
            >
              생산 완료
            </Button>
          </>
        )}

        {isStatusCompleted && (
          <Chip
            icon={<CheckCircleIcon />}
            label="생산 완료됨"
            color="success"
            variant="filled"
          />
        )}
      </DialogActions>
    </BootstrapDialog>
  );
};

export default ProductionDetailDialog;
